import React from 'react';
import styled, { keyframes } from 'styled-components';
import { smMobile } from 'Utils/utilsStyle';
import { IsMobile } from 'Utils/UtilityFunctions';

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBox = styled.div<{ width?: string; height?: string; margin?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '16px'};
  margin: ${(props) => props.margin || '0'};
`;

const Wrapper = styled.div<{ $isMobile?: boolean }>`
  overflow: ${($isMobile) => $isMobile && ' hidden'};
  .feed-highlights-skeleton {
    display: flex;
    transition: all 0.2s ease;
  }

  .avatar-area {
    margin-right: 12px;
    flex-shrink: 0;
  }

  .avatar-skeleton {
    border-radius: 50%;
    @media all and (max-width: ${smMobile}) {
      width: 40px;
      height: 40px;
    }
  }

  .content-area {
    flex: 1;
    min-width: 0;
  }

  .header-area {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .footer-area {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
  }

  .badge-skeleton {
    width: 60px;
    height: 24px;
    border-radius: 12px;
  }

  .action-skeleton {
    width: 80px;
    height: 14px;
  }
`;

type Props = {
  variant?: 'default' | 'withImage' | 'withChips';
  count?: number;
  showHeader?: boolean;
  showFooter?: boolean;
  showTime?: boolean;
};

const FeedHighlightsSkeleton: React.FC<Props> = ({
  count = 1,
  showHeader = true,
  showFooter = true,
}) => {
  const isMobile = IsMobile();
  const renderSkeletonItem = (key: number) => (
    <Wrapper key={key} $isMobile={isMobile}>
      <div className="feed-highlights-skeleton">
        {/* Avatar Section */}
        <div className="avatar-area">
          <SkeletonBox className="avatar-skeleton" width="48px" height="48px" />
        </div>

        {/* Content Section */}
        <div className="content-area">
          {/* Header with title and time */}
          {showHeader && (
            <div className="header-area">
              <SkeletonBox className="title-skeleton" width="100%" height="18px" />
            </div>
          )}

          {/* Footer with badges and actions */}
          {showFooter && (
            <div className="footer-area">
              <SkeletonBox className="badge-skeleton" width="60px" height="24px" />
              <SkeletonBox className="action-skeleton" width="80px" height="14px" />
              <SkeletonBox className="action-skeleton" width="80px" height="14px" />
              <SkeletonBox className="action-skeleton" width="80px" height="14px" />
              <SkeletonBox className="action-skeleton" width="80px" height="14px" />
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );

  return <>{Array.from({ length: count }, (_, i) => renderSkeletonItem(i))}</>;
};

export default FeedHighlightsSkeleton;